import React, { useEffect, useState } from "react";
import { Users, UserRound, MessageSquareText } from "lucide-react";
import {
  getTemplates,
  sendBulkNotification,
  getRecipients,
  getLoanTypes,
} from "../../../../src/api/communicationTemplates";
import { FaCloudUploadAlt } from "react-icons/fa";
import { toast } from "react-toastify";

function BulkMessagingTab({ preSelectedTemplate }) {
  const recipientGroups = [
    "All",
    "Customers",
    "Banking Partners",
    "Affiliate Partners",
    "Network",
  ];

  const [recipients, setRecipients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedTemplateObj, setSelectedTemplateObj] = useState(null);
  const [recipientGroup, setRecipientGroup] = useState("");
  const [loanTypes, setLoanTypes] = useState([]);
  const [selectedLoanType, setSelectedLoanType] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [mode, setMode] = useState(["in-app"]);
  const [sendLater, setSendLater] = useState(false);
  const [sendDate, setSendDate] = useState("");
  const [sendHour, setSendHour] = useState(() => {
    const now = new Date();
    return (now.getHours() % 12 || 12).toString();
  });
  const [sendMinute, setSendMinute] = useState(() => {
    const now = new Date();
    return now.getMinutes().toString().padStart(2, "0");
  });
  const [sendAmPm, setSendAmPm] = useState(() => {
    const now = new Date();
    return now.getHours() >= 12 ? "PM" : "AM";
  });
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editableMessage, setEditableMessage] = useState("");
  const [whatsAppProgress, setWhatsAppProgress] = useState({
    total: 0,
    sent: 0,
    failed: 0,
  });
  const [fetchingRecipients, setFetchingRecipients] = useState(false);

  const isCustomTemplate = selectedTemplate === "custom";

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    fetchRecipients();
  }, [recipientGroup, selectedLoanType]);

  useEffect(() => {
    const found = templates.find((t) => t._id === selectedTemplate);
    setSelectedTemplateObj(found || null);
    if (found) {
      setEditableMessage(found.body || "");
    } else {
      setEditableMessage("");
    }
  }, [selectedTemplate, templates]);

  // Handle pre-selected template from template send action
  useEffect(() => {
    if (preSelectedTemplate && templates.length > 0) {
      setSelectedTemplate(preSelectedTemplate._id);
      setSelectedTemplateObj(preSelectedTemplate);
      setEditableMessage(preSelectedTemplate.body || "");
    }
  }, [preSelectedTemplate, templates]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [templatesRes, loanTypesRes] = await Promise.all([
        getTemplates(),
        getLoanTypes(),
      ]);
      setTemplates(templatesRes || []);
      setLoanTypes(loanTypesRes.data || []);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipients = async () => {
    console.log('fetchRecipients called with:', { recipientGroup, selectedLoanType });
    
    if (!recipientGroup) {
      setRecipients([]);
      return;
    }

    setFetchingRecipients(true);
    try {
      let recipientType = "";
      switch (recipientGroup) {
        case "All":
          recipientType = "all";
          break;
        case "Customers":
          recipientType = "customer";
          break;
        case "Banking Partners":
          recipientType = "banking-partner";
          break;
        case "Affiliate Partners":
          recipientType = "affiliate-partner";
          break;
        case "Network":
          recipientType = "staff";
          break;
        default:
          recipientType = recipientGroup.toLowerCase().replace(" ", "-");
      }

      const params = {
        recipientType,
        page: 1,
        limit: 1000,
      };

      if (recipientType === "customer" && selectedLoanType) {
        params.loanType = selectedLoanType;
      }

      console.log('Fetching recipients with params:', params);
      const response = await getRecipients(params);
      console.log('Recipients response:', response);
      
      if (response && response.recipients) {
        setRecipients(response.recipients);
        console.log('Set recipients count:', response.recipients.length);
      } else {
        console.log('No recipients in response:', response);
        setRecipients([]);
      }
    } catch (err) {
      console.error("Error fetching recipients:", err);
      console.error("Error details:", err.response?.data);
      setRecipients([]);
      toast.error(`Failed to fetch recipients: ${err.message}`);
    } finally {
      setFetchingRecipients(false);
    }
  };

  const handleModeToggle = (type) => {
    setMode((prev) =>
      prev.includes(type) ? prev.filter((m) => m !== type) : [...prev, type]
    );
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      const maxFileSize = 5 * 1024 * 1024; // 5MB

      if (file.size > maxFileSize) {
        alert("File is too large. Maximum size is 5MB.");
        e.target.value = null;
        return;
      }

      if (allowedTypes.includes(file.type)) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setAttachmentPreview(reader.result);
          setAttachment(reader.result); // Store base64 string
        };
        reader.readAsDataURL(file);
      } else {
        alert(
          "Invalid file type. Please upload JPG, JPEG, or PNG images only."
        );
        e.target.value = null;
        setAttachment(null);
        setAttachmentPreview(null);
      }
    }
  };

  const handleSendNow = async () => {
    console.log('handleSendNow called');
    console.log('Current state:', {
      recipientGroup,
      recipients: recipients.length,
      selectedTemplate,
      isCustomTemplate,
      customMessage: customMessage?.substring(0, 50),
      editableMessage: editableMessage?.substring(0, 50),
      mode
    });
    
    setSending(true);
    setSendResult(null);
    setWhatsAppProgress({ total: 0, sent: 0, failed: 0 });

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("You must be logged in to send messages");
        setSending(false);
        return;
      }

      // Calculate scheduled date/time
      let scheduledDateTime = null;
      if (sendLater && sendDate && sendHour && sendMinute) {
        let hour24 = parseInt(sendHour, 10);
        if (sendAmPm === "PM" && hour24 !== 12) hour24 += 12;
        if (sendAmPm === "AM" && hour24 === 12) hour24 = 0;
        const timeString = `${hour24
          .toString()
          .padStart(2, "0")}:${sendMinute.padStart(2, "0")}:00`;
        scheduledDateTime = new Date(`${sendDate}T${timeString}`);
        if (isNaN(scheduledDateTime.getTime())) {
          throw new Error("Invalid scheduled date/time");
        }
      }

      // Handle WhatsApp messaging
      if (mode.includes("whatsapp")) {
        console.log('Starting WhatsApp messaging...');
        setWhatsAppProgress((prev) => ({
          ...prev,
          total: recipients.length,
        }));

        const recipientIds = recipients.map((r) => r._id);
        // Fix recipient type mapping for WhatsApp
        let whatsappRecipientType = "all";
        switch (recipientGroup) {
          case "Customers":
            whatsappRecipientType = "customer";
            break;
          case "Banking Partners":
            whatsappRecipientType = "banking-partner";
            break;
          case "Affiliate Partners":
            whatsappRecipientType = "affiliate-partner";
            break;
          case "Network":
            whatsappRecipientType = "staff";
            break;
          default:
            whatsappRecipientType = "all";
        }

        const whatsappPayload = {
          recipientType: whatsappRecipientType,
          userIds: recipientIds,
          message: isCustomTemplate ? customMessage : editableMessage,
        };

        console.log('WhatsApp payload:', whatsappPayload);
        console.log('Token:', token ? 'Present' : 'Missing');

        try {
          const response = await fetch(
            "https://dashboard.lono.co.in/api/whatsapp/send-bulk",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(whatsappPayload),
            }
          );

          console.log('WhatsApp response status:', response.status);
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error('WhatsApp API error response:', errorText);
            throw new Error(`WhatsApp API error: ${response.status} - ${errorText}`);
          }

          const result = await response.json();
          console.log('WhatsApp result:', result);
          
          // Log detailed failure info
          if (result.results) {
            const failedResults = result.results.filter(r => r.status === 'failed');
            if (failedResults.length > 0) {
              console.log('Failed WhatsApp sends:', failedResults);
              failedResults.forEach(failed => {
                console.log(`Failed send to ${failed.whatsappNumber}:`, failed.response);
              });
            }
          }

          if (result.summary) {
            setWhatsAppProgress({
              total: result.summary.total,
              sent: result.summary.sent,
              failed: result.summary.failed,
            });
          } else if (result.results && Array.isArray(result.results)) {
            const sentCount = result.results.filter((r) => r.status === "sent").length;
            const failedCount = result.results.filter((r) => r.status === "failed").length;

            setWhatsAppProgress({
              total: recipients.length,
              sent: sentCount,
              failed: failedCount,
            });
          }

          if (result.message) toast.success(result.message);
        } catch (whatsappError) {
          console.error('WhatsApp messaging failed:', whatsappError);
          toast.error(`WhatsApp error: ${whatsappError.message}`);
        }
      }

      // Handle in-app/email messaging
      if (mode.includes("in-app") || mode.includes("email")) {
        const customerIds = recipients
          .filter((r) => r.userType === "customer")
          .map((r) => r._id);
        const userIds = recipients
          .filter((r) => r.userType !== "customer")
          .map((r) => r._id);

        const payload = {
          recipientType: recipientGroup?.toLowerCase().replace(" ", "-") || "all",
          templateId: isCustomTemplate ? null : selectedTemplate,
          customMessage: isCustomTemplate ? customMessage : editableMessage,
          scheduledFor: scheduledDateTime
            ? scheduledDateTime.toISOString()
            : null,
          mode: mode.filter((m) => m !== "whatsapp"),
          attachmentUrl: attachment || null,
        };

        if (customerIds.length) payload.customerIds = customerIds;
        if (userIds.length) payload.userIds = userIds;

        const res = await sendBulkNotification(payload, token);
        toast.success(res.message || "Message sent successfully");
      }

      setSendResult({
        success: true,
        message: "Messages sent successfully",
      });

      // Reset form
      setSelectedTemplate("");
      setCustomMessage("");
      setEditableMessage("");
      setAttachment(null);
      setAttachmentPreview(null);
      setSendLater(false);
      setSendDate("");
      const now = new Date();
      setSendHour((now.getHours() % 12 || 12).toString());
      setSendMinute(now.getMinutes().toString().padStart(2, "0"));
      setSendAmPm(now.getHours() >= 12 ? "PM" : "AM");
      setRecipientGroup("");
      setSelectedLoanType("");
      setMode(["in-app"]);
      if (document.getElementById("upload")) {
        document.getElementById("upload").value = "";
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Failed to send";
      console.error("Bulk notification error:", err);
      console.error("Error details:", {
        message: err.message,
        response: err?.response?.data,
        status: err?.response?.status
      });
      toast.error(errorMessage);
      setSendResult({ success: false, message: errorMessage });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 flex justify-center items-center text-gray-400">
        Loading...
      </div>
    );
  }

  return (
    <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Recipients */}
      <div className="bg-black border border-gray-800 rounded-lg p-6">
        <h2 className="text-xl mb-6 flex items-center text-[#FFCA20]">
          <Users className="w-5 h-5 mr-2" /> Select Recipients
        </h2>

        <div className="mb-4">
          <label className="block text-gray-300 mb-2">Recipients</label>
          <select
            className="w-full p-3 bg-black border border-[#333] rounded-lg text-gray-300"
            value={recipientGroup}
            onChange={(e) => setRecipientGroup(e.target.value)}
          >
            <option value="">Select</option>
            {recipientGroups.map((group) => (
              <option key={group} value={group}>
                {group}
              </option>
            ))}
          </select>
        </div>

        {recipientGroup === "Customers" && (
          <div className="mb-4">
            <label className="block text-gray-300 mb-2">Loan Type</label>
            <select
              className="w-full p-3 bg-black border border-[#333] rounded-lg text-gray-300"
              value={selectedLoanType}
              onChange={(e) => setSelectedLoanType(e.target.value)}
            >
              <option value="">All Loan Types</option>
              {loanTypes.map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="bg-yellow-900 p-4 rounded-lg flex justify-between items-center">
          <div>
            <div className="text-[#FFCA20] text-sm">SELECTED RECIPIENTS</div>
            <div className="text-2xl font-bold">
              {fetchingRecipients ? "Loading..." : recipients.length}
            </div>
            {recipientGroup && !fetchingRecipients && recipients.length === 0 && (
              <div className="text-red-400 text-xs mt-1">No recipients found</div>
            )}
          </div>
          <div className="bg-yellow-400 p-3 rounded-full text-black">
            <UserRound className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Messaging */}
      <div className="lg:col-span-2 bg-black border border-gray-800 rounded-lg p-6">
        <h2 className="text-xl mb-6 flex items-center text-[#FFCA20]">
          <MessageSquareText className="w-5 h-5 mr-2" /> Bulk Message
        </h2>

        <div className="mb-4">
          <label className="block text-gray-300 mb-2">Template</label>
          <select
            className="w-full p-3 bg-black border border-[#333] rounded-lg text-gray-300"
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
          >
            <option value="">Select Message Template</option>
            <option value="custom" className="text-[#FFCA20]">
              + Custom Message
            </option>
            {templates.map((t) => (
              <option key={t._id} value={t._id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        {/* Modes */}
        <div className="mb-4">
          <label className="block text-gray-300 mb-2">
            Mode of Communication
          </label>
          <div className="flex flex-wrap gap-3">
            {["in-app", "email", "whatsapp"].map((type) => (
              <label
                key={type}
                className={`flex gap-2 items-center p-3 border rounded-lg ${
                  mode.includes(type)
                    ? "border-yellow-400 border-2 text-[#FFCA20]"
                    : "border-gray-600 text-gray-500"
                } cursor-pointer`}
              >
                <input
                  type="checkbox"
                  className="appearance-none h-5 w-5 border-2 border-white/20 rounded-sm bg-transparent checked:bg-transparent checked:border-yellow-400 checked:appearance-none relative cursor-pointer
            before:content-['✓'] before:absolute before:text-[#ffca20] before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                  checked={mode.includes(type)}
                  onChange={() => handleModeToggle(type)}
                />
                {type === "in-app" ? "In-App Notification" : type === "whatsapp" ? "WhatsApp" : type.toUpperCase()}
              </label>
            ))}
          </div>
        </div>

        {/* Message Input OR Preview */}
        {isCustomTemplate ? (
          <div className="mb-4 flex flex-col md:flex-row gap-6">
            <div className="flex-1">
              <label className="block text-gray-300 mb-2">Message</label>
              <textarea
                rows={4}
                className="w-full p-3 bg-black border border-[#333] text-gray-300 rounded-lg"
                placeholder="Enter your message here"
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
              />
            </div>
            <div className="w-full md:w-1/3">
              <label className="block text-gray-300 mb-2">Attachment</label>
              <div className="border border-[#333] border-dashed p-6 rounded-lg text-center text-gray-300">
                <input
                  type="file"
                  className="hidden"
                  id="upload"
                  accept=".jpg,.jpeg,.png"
                  onChange={handleFileChange}
                />
                <label
                  htmlFor="upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <FaCloudUploadAlt className="text-[#FFCA20] text-3xl mb-2" />
                  <div className="text-[#FFCA20] mb-1">Click to Upload</div>
                  <div className="text-sm text-gray-500">or Drag and Drop</div>
                  {attachmentPreview && (
                    <div className="mt-2 text-white text-xs flex flex-col items-center gap-2">
                      <img
                        src={attachmentPreview}
                        alt="Preview"
                        className="w-16 h-16 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setAttachment(null);
                          setAttachmentPreview(null);
                          document.getElementById("upload").value = "";
                        }}
                        className="text-red-500 hover:text-red-400 text-sm font-bold"
                        title="Remove"
                      >
                        Remove Image
                      </button>
                    </div>
                  )}
                </label>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-4">
            <label className="block text-gray-300 mb-2">Message</label>
            <textarea
              rows={4}
              className="w-full p-3 bg-black border border-[#333] text-gray-300 rounded-lg"
              value={editableMessage}
              onChange={(e) => setEditableMessage(e.target.value)}
              placeholder="Edit the message before sending"
            />
          </div>
        )}

        {/* Delivery Options */}
        <div className="mb-4">
          <label className="block text-gray-300 mb-2">Delivery Options</label>
          <div className="flex items-center gap-6">
            {["Send Now", "Send Later"].map((option, idx) => {
              const value = idx === 1;
              return (
                <label
                  key={option}
                  className="flex items-center cursor-pointer"
                >
                  <input
                    type="radio"
                    name="delivery"
                    checked={sendLater === value}
                    onChange={() => setSendLater(value)}
                    className="hidden"
                  />
                  <span
                    className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${
                      sendLater === value
                        ? "border-yellow-400"
                        : "border-gray-400"
                    }`}
                  >
                    {sendLater === value && (
                      <span className="w-2.5 h-2.5 bg-yellow-400 rounded-full" />
                    )}
                  </span>
                  <span
                    className={
                      sendLater === value ? "text-yellow-400" : "text-gray-300"
                    }
                  >
                    {option}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {sendLater && (
          <div className="flex gap-4 mb-4">
            <div
              className="flex-1 relative cursor-pointer"
              onClick={() => document.getElementById("date-input").showPicker()}
            >
              <input
                id="date-input"
                type="date"
                value={sendDate}
                onChange={(e) => setSendDate(e.target.value)}
                className="w-full p-2 bg-black border border-[#333] text-gray-300 rounded cursor-pointer"
                min={new Date().toISOString().split("T")[0]}
                placeholder="Select date"
              />
            </div>
            <div className="flex-1 flex gap-2">
              <select
                value={sendHour}
                onChange={(e) => setSendHour(e.target.value)}
                className="flex-1 p-2 bg-black border border-[#333] text-gray-300 rounded"
              >
                {Array.from({ length: 12 }, (_, i) => {
                  const hour = i + 1;
                  return (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  );
                })}
              </select>
              <select
                value={sendMinute}
                onChange={(e) => setSendMinute(e.target.value)}
                className="flex-1 p-2 bg-black border border-[#333] text-gray-300 rounded"
              >
                {Array.from({ length: 60 }, (_, i) => {
                  const minute = i.toString().padStart(2, "0");
                  return (
                    <option key={minute} value={minute}>
                      {minute}
                    </option>
                  );
                })}
              </select>
              <select
                value={sendAmPm}
                onChange={(e) => setSendAmPm(e.target.value)}
                className="w-20 p-2 bg-black border border-[#333] text-gray-300 rounded"
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </div>
        )}

        <button
          className="w-full mt-4 bg-yellow-400 text-black py-3 rounded-lg font-bold disabled:opacity-60"
          onClick={handleSendNow}
          disabled={
            sending ||
            !recipientGroup ||
            recipients.length === 0 ||
            (!isCustomTemplate && !selectedTemplate) ||
            (isCustomTemplate && !customMessage.trim()) ||
            (!isCustomTemplate && !editableMessage.trim()) ||
            mode.length === 0 ||
            (sendLater && (!sendDate || !sendHour || !sendMinute))
          }
        >
          {sending ? "Sending..." : sendLater ? "Schedule" : "Send Now"}
        </button>

        {mode.includes("whatsapp") && sending && (
          <div className="mt-4 p-3 bg-gray-900 text-yellow-400 rounded-lg">
            WhatsApp Progress: {whatsAppProgress.sent} sent / {whatsAppProgress.failed} failed / {whatsAppProgress.total} total
          </div>
        )}

        {/* {sendResult && (
          <div
            className={`mt-4 p-3 rounded-lg ${
              sendResult.success
                ? "bg-green-900 text-green-400"
                : "bg-red-900 text-red-400"
            }`}
          >
            {sendResult.message}
          </div>
        )} */}
      </div>
    </div>
  );
}

export default BulkMessagingTab;
